import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Badge,
  Checkbox,
  List,
  ListItem,
  ListIcon,
  Divider,
} from '@chakra-ui/react';
import { WarningIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';

const ValidationItem = ({ count, label, icon, color }) => (
  count > 0 ? (
    <ListItem display="flex" alignItems="center" color={color}>
      <ListIcon as={icon} color={color} />
      <Text>{`${label}: ${count}`}</Text>
    </ListItem>
  ) : null
);

const TransferModal = ({
  isOpen,
  onClose,
  selectedRecords,
  validationSummary,
  transferMode,
  testEmail,
  onUnselectIncomplete,
  onTransfer,
}) => {
  const {
    totalSelected,
    complete,
    incomplete,
    missingFirstName,
    missingLastName,
    missingEmail,
    invalidEmail,
    missingPhone,
    invalidPhone,
  } = validationSummary;

  const hasIncomplete = incomplete > 0;
  const canTransfer = complete > 0 && !hasIncomplete;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Transfer Validation Summary</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Selected Records Summary */}
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                Selected Records: {totalSelected}
              </Text>
            </Box>

            <Divider />

            {/* Complete Records */}
            <Box>
              <HStack spacing={2} mb={2}>
                <CheckCircleIcon color="green.500" />
                <Text fontWeight="bold">Complete Records: {complete}</Text>
              </HStack>
              {complete > 0 && (
                <Text fontSize="sm" color="gray.600" ml={6}>
                  Ready for transfer
                </Text>
              )}
            </Box>

            {/* Incomplete Records */}
            {hasIncomplete && (
              <Box>
                <HStack spacing={2} mb={2}>
                  <WarningIcon color="orange.500" />
                  <Text fontWeight="bold">Incomplete Records: {incomplete}</Text>
                </HStack>
                <List spacing={1} ml={6}>
                  <ValidationItem
                    count={missingFirstName}
                    label="Missing First Name"
                    icon={InfoIcon}
                    color="orange.500"
                  />
                  <ValidationItem
                    count={missingLastName}
                    label="Missing Last Name"
                    icon={InfoIcon}
                    color="orange.500"
                  />
                  <ValidationItem
                    count={missingEmail}
                    label="Missing Email"
                    icon={InfoIcon}
                    color="orange.500"
                  />
                  <ValidationItem
                    count={invalidEmail}
                    label="Invalid Email Format"
                    icon={WarningIcon}
                    color="red.500"
                  />
                  <ValidationItem
                    count={missingPhone}
                    label="Missing Phone"
                    icon={InfoIcon}
                    color="orange.500"
                  />
                  <ValidationItem
                    count={invalidPhone}
                    label="Invalid Phone Format"
                    icon={WarningIcon}
                    color="red.500"
                  />
                </List>
                <Button
                  size="sm"
                  colorScheme="orange"
                  variant="outline"
                  onClick={onUnselectIncomplete}
                  mt={2}
                  ml={6}
                >
                  Unselect Incomplete Records
                </Button>
              </Box>
            )}

            <Divider />

            {/* Transfer Mode */}
            <Box>
              <HStack spacing={2}>
                <InfoIcon color="blue.500" />
                <Text fontWeight="bold">Current Mode:</Text>
                <Badge
                  colorScheme={transferMode === 'test' ? 'purple' : 'green'}
                  fontSize="0.9em"
                  px={2}
                  py={1}
                >
                  {transferMode.toUpperCase()}
                </Badge>
              </HStack>
              {transferMode === 'test' && (
                <Text fontSize="sm" color="gray.600" ml={6} mt={1}>
                  All emails will be sent to: {testEmail}
                </Text>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!canTransfer}
            onClick={onTransfer}
          >
            Transfer Complete Records
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransferModal;
